/**
 * @packageDocumentation
 * @module      CorPageRoboticNetworks.ts
 * @description The Robotic Networks page for this application.
 * @copyright   Copyright (c) 2021 by Corindus Inc., a Siemens Healthineers Company. All rights reserved.
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var CorPageRoboticNetworksCard_1;
import { css, customElement, html, internalProperty, property } from 'lit-element';
import CloudApi from "./CloudApi";
import Fetcher from "./Fetcher";
import RoboticNetwork from "./RoboticNetwork";
import "./CorSystemState";
import { CorPageEntityMaintenance, ItemChosenEvent, CardStatus, ItemAddedOrUpdatedEvent, ShowPopoverEvent, CorPageEntityMaintenanceList, CorPageEntityMaintenanceCard } from "./CorPageEntityMaintenance";
let corPageRoboticNetworksFetcher = new Fetcher();
/** Object through which all API access occurs. In unit tests, overwrite this with a mock. */
export { corPageRoboticNetworksFetcher };
/** The Robotic Networks page, comprised of a list on the left and a card for the selected network on the right. */
let CorPageRoboticNetworks = class CorPageRoboticNetworks extends CorPageEntityMaintenance {
    get selectorForList() {
        return 'cor-page-robotic-networks-list';
    }
    get selectorForCard() {
        return 'cor-page-robotic-networks-card';
    }
    get blankCardImageFile() {
        return 'robotic_network.svg';
    }
    get singleItemName() {
        return 'robotic network';
    }
    render() {
        let cardContents = html `
                ${this.cardStatus == CardStatus.Hidden ?
            this.blankCardHtml :
            html `
                    <cor-page-robotic-networks-card id="card"
                            itemid="${this.itemId}"
                    ></cor-page-robotic-networks-card>`}
            `;
        return html `
            <sh-page id="ThePage"  theme="${this.currentTheme}">
                ${this.popoverHtml}
                <sh-card slot="left" id="list">
                    <cor-page-robotic-networks-list
                            activeitemid="${this.itemId}">
                    </cor-page-robotic-networks-list>
                    <div slot="footer">
                        <sh-button label='Refresh'
                                   ?disabled="${!this.okToRefresh}"
                                   color="secondary"
                                   @click=${() => this.refreshList()}>
                        </sh-button>
                        <sh-button label='Add'
                                   ?disabled="${!this.okToAdd}"
                                   @click=${() => this.addItem()}>
                        </sh-button>
                    </div>
                </sh-card>
                ${cardContents}
            </sh-page>`;
    }
};
CorPageRoboticNetworks = __decorate([
    customElement('cor-page-robotic-networks')
], CorPageRoboticNetworks);
export { CorPageRoboticNetworks };
/**
 * The list of robotic networks.
 */
let CorPageRoboticNetworksList = class CorPageRoboticNetworksList extends CorPageEntityMaintenanceList {
    summarySortCompare(a, b) {
        return a.roboticNetworkName.localeCompare(b.roboticNetworkName);
    }
    getSummaries() {
        return new CloudApi(corPageRoboticNetworksFetcher, this).getRoboticNetworkSummaries();
    }
    getItemIdAsStringFromSummary(summary) {
        return "" + summary.roboticNetworkId;
    }
    /** Renders the component as part of LitElement's lifecycle. */
    render() {
        this.Pages = this.calculatePages();
        return html `
            <sh-text class ="paddingAll" size="body-2">${this.itemsList.length} robotic networks</sh-text>
            <sh-table id="robotic-networks-table" ?readonly=${!this.okToSelectNewItemInList}>
                <sh-table-row slot="header">
                    <sh-table-head columns="18" unsortable>Name</sh-table-head>
                    <sh-table-head columns="6" unsortable>Systems</sh-table-head>
                </sh-table-row>
                ${this.itemsList.map((item, index) => this.isWithinPageRange(index) ? html `
                    <sh-table-row
                        id="${this.databaseIdToHtmlId.get(this.getItemIdAsStringFromSummary(item))}"
                        ?active=${this.activeItemId === "" + item.roboticNetworkId}
                        ?lastAddedOrUpdated=${this.lastAddedOrUpdatedId === "" + item.roboticNetworkId}
                        @click=${() => this.dispatchEvent(new ItemChosenEvent(item.roboticNetworkId))}>
                        <sh-table-cell columns="18" title="${item.roboticNetworkName}">
                            <sh-text class="list-text-wrapper"> ${item.roboticNetworkName} </sh-text>
                        </sh-table-cell>
                        <sh-table-cell columns="6" >
                            <sh-text class="list-text-wrapper"> ${item.systemCount} </sh-text>
                        </sh-table-cell>
                    </sh-table-row>` : html ``)}
            </sh-table>
            <sh-pagination pages="${this.Pages}" current-page="${this.currentPage}" @click="${this.changeCurrentPage}"> </sh-pagination>               
        `;
    }
};
CorPageRoboticNetworksList = __decorate([
    customElement('cor-page-robotic-networks-list')
], CorPageRoboticNetworksList);
export { CorPageRoboticNetworksList };
/**
 * A robotic network that is being added or updated.
 *
 * Designed to be a child of cor-page-robotic-networks; only exported for the sake of unit testing.
 */
let CorPageRoboticNetworksCard = CorPageRoboticNetworksCard_1 = class CorPageRoboticNetworksCard extends CorPageEntityMaintenanceCard {
    constructor() {
        super(...arguments);
        this.corPageList = new CorPageRoboticNetworksList();
        this.availableSystems = [];
        this.oldRoboticNetworkName = '';
        this.confirmingRemoveSystems = false;
        this.addingSystems = false;
        this.selectedSystems = new Set();
    }
    static get styles() {
        return css `
            ${CorPageRoboticNetworksCard_1.entityMaintenanceCardStyles}
            
            sh-input-text, sh-dropdown {
              height: 70px;
            }

            div.row {
                width: 100%;
                height: 70px;
            }

            #RoboticNetworkInfo {
              display: flex;
              align-items: center;
            }
          
            #RoboticNetworkInfo > sh-button:first-of-type {
              margin-left: auto;
            }
              
            #RoboticNetworkName {
               width: 400px;
            }

            sh-checkbox {
              align-self: center;
            }
          
            #add-systems-table {
              height: 50vh;
            }
            `;
    }
    constructItem() {
        return new RoboticNetwork();
    }
    getItem(roboticNetworkIdAsString) {
        return new CloudApi(corPageRoboticNetworksFetcher, this).getRoboticNetwork(+roboticNetworkIdAsString);
    }
    deleteItem(item) {
        return new CloudApi(corPageRoboticNetworksFetcher, this).deleteRoboticNetwork(item.roboticNetworkId);
    }
    get genericItemName() {
        return "robotic network";
    }
    get currentItemName() {
        return this.workItem.roboticNetworkName;
    }
    get selectedSystemsCount() {
        return this.selectedSystems.size;
    }
    get maxSelectedSystems() {
        return this.managing ? this.workItem.systems.length : 0;
    }
    get sortedSelectedSystemNames() {
        let value = [];
        value.push(...this.selectedSystems);
        value = value.map((s) => s.systemName).sort((a, b) => a.localeCompare(b));
        return value;
    }
    get removeSystemsConfirmationLabel() {
        return "Remove " +
            (this.selectedSystemsCount == 1 ?
                "the selected system?" :
                "these " + this.selectedSystemsCount + " systems?");
    }
    get roboticNetworkName() {
        return this.workItem.roboticNetworkName || '';
    }
    set roboticNetworkName(newValue) {
        this.setterBody(newValue, 'roboticNetworkName', this.workItem);
    }
    save() {
        let api = new CloudApi(corPageRoboticNetworksFetcher, this);
        let that = this;
        api.saveRoboticNetwork(this.workItem).then((response) => {
            if (response === null || response === void 0 ? void 0 : response.ok) {
                response.json().then((responseRoboticNetwork) => {
                    that.itemId = responseRoboticNetwork.roboticNetworkId;
                    const isAdd = that.adding;
                    if (isAdd) {
                        that.dispatchEvent(new ShowPopoverEvent("Robotic Network Created", "The robotic network is created. To add systems to the network, press 'Manage Network'."));
                        that.changeCardStatus(CardStatus.Viewing); // Let user add systems now.
                    }
                    else if (that.editing) {
                        this.dispatchEvent(new ShowPopoverEvent("Network Changes Saved", `"${this.oldRoboticNetworkName}\" changed name to \"${this.workItem.roboticNetworkName}\"`));
                        that.changeCardStatus(CardStatus.Viewing);
                    }
                    else if (that.managing) {
                        this.dispatchEvent(new ShowPopoverEvent((this.addingSystems ? 'Systems Added to Network' : 'Systems Removed from Network'), `${this.selectedSystems.size} ${this.selectedSystems.size == 1 ? "system" : "systems"} ${this.addingSystems ? "added to" : "removed from"} ${this.workItem.roboticNetworkName}`));
                        that.confirmingRemoveSystems = false;
                        that.addingSystems = false;
                        that.selectedSystems.clear();
                        that.changeCardStatus(CardStatus.Viewing);
                    }
                    // @ts-ignore because that.itemId will not be null
                    that.dispatchEvent(new ItemAddedOrUpdatedEvent(that.itemId, isAdd));
                });
            }
        });
    }
    manageNetwork() {
        this.changeCardStatus(CardStatus.Managing);
        if (this.workItem.systems.length === 0) {
            this.initiateAddingSystems();
        }
    }
    initiateRemoveSystems() {
        this.confirmingRemoveSystems = true;
    }
    doRemoveSystems() {
        this.workItem.systems = this.workItem.systems.filter((system) => !this.selectedSystems.has(system));
        this.save();
    }
    cancelRemoveSystems() {
        this.confirmingRemoveSystems = false;
        this.selectedSystems.clear();
    }
    initiateAddingSystems() {
        this.availableSystems = [];
        new CloudApi(corPageRoboticNetworksFetcher, this).getSystems()
            .then((systems) => {
            let systemIdsInNetwork = [];
            this.workItem.systems.forEach((s) => systemIdsInNetwork.push(s.systemId));
            this.availableSystems = systems
                .filter(s => !systemIdsInNetwork.includes(s.systemId))
                .sort((a, b) => a.systemName.localeCompare(b.systemName));
            this.selectedSystems.clear();
            this.addingSystems = true;
        }, () => {
            this.dispatchEvent(new ShowPopoverEvent("Get Available Systems", "The available systems could not be retrieved due to an error on the server.", false));
        });
    }
    cancelAddSystems() {
        this.addingSystems = false;
        this.selectedSystems.clear();
    }
    doAddSystems() {
        this.workItem.systems.push(...this.selectedSystems);
        this.workItem.systems.sort((a, b) => a.systemName.localeCompare(b.systemName));
        this.save();
    }
    addSystemSelectionsChanged(e) {
        this.selectedSystems.clear();
        // @ts-ignore
        if (e.currentTarget.active) {
            this.availableSystems.forEach((s) => {
                this.selectedSystems.add(s);
            });
        }
        this.requestUpdate();
    }
    systemSelectionChanged(e, system) {
        // @ts-ignore
        if (e.currentTarget.active) {
            this.selectedSystems.add(system);
        }
        else {
            this.selectedSystems.delete(system);
        }
        this.requestUpdate();
    }
    allSystemSelectionsChanged(e) {
        this.selectedSystems.clear();
        // @ts-ignore
        if (e.currentTarget.active) {
            this.workItem.systems.forEach((s) => {
                this.selectedSystems.add(s);
            });
        }
        this.requestUpdate();
    }
    initiateEdit() {
        this.validationTracker.resetPreviousValidationResults();
        this.workItemPrevious = JSON.parse(JSON.stringify(this.workItem));
        this.changeCardStatus(CardStatus.Editing);
        this.oldRoboticNetworkName = this.workItem.roboticNetworkName;
    }
    cancelManaging() {
        this.selectedSystems.clear();
        this.changeCardStatus(CardStatus.Viewing);
    }
    render() {
        const self = this;
        this.workItem.systems.sort((a, b) => a.systemName.localeCompare(b.systemName));
        return html `
            <sh-card id="card" label="${this.adding ? 'Add New Robotic Network' : this.workItem.roboticNetworkName}">
                <div>
                    <sh-text class ="paddingAll" size="title-1">Robotic Network Information</sh-text>
                    <div class="row" id="RoboticNetworkInfo">
                        <sh-input-text id="RoboticNetworkName" label="Robotic Network Name"
                                       autofocus
                                       ?readonly=${self.viewing || self.managing}
                                       ?mandatory="${self.validationTracker.alwaysMandatoryString('roboticNetworkName', () => { return this.roboticNetworkName; }, () => { return this.workItemPrevious.roboticNetworkName; })}"
                                       value="${self.roboticNetworkName}"
                                       ?error = "${this.validationTracker.error("roboticNetworkName", () => {
            return !((this.hasFieldValueChanged(this, "roboticNetworkName")) &&
                (this.corPageList.isItemAvailable(self.roboticNetworkName, "roboticNetworkName")));
        })}"
                                       error-message = "This Robotic Network Name is already present."
                                       @input=${self.inputHelper('roboticNetworkName')}>
                        </sh-input-text>
                        
                        ${self.viewing ?
            html `
                                <sh-button label='Edit'
                                           color="tertiary"
                                           @click=${self.initiateEdit} >
                                </sh-button>` : html ``}
                        ${self.editing || self.adding ?
            html `
                                <sh-button id='Cancel' label='Cancel'
                                           color="tertiary"
                                           @click=${self.cancelAddOrEdit}>
                                </sh-button>
                                <sh-button id='Save' label='Save'
                                           color="primary"
                                           @click=${self.save}
                                           ?disabled=${self.validationTracker.isSaveNotAllowed(self.editing)}>
                                </sh-button>` : html ``}
                    </div>
                    ${!self.adding ? html `
                        <sh-divider></sh-divider>
                        <sh-text class="paddingBottom24Pixel" size="title-1">Systems in Network</sh-text>
                        ${this.managing ?
            html `<sh-tag class ="paddingBottom24Pixel paddingTop24Pixel" label="${this.selectedSystems.size} selected"></sh-tag>` :
            html `<sh-text class="paddingBottom24Pixel paddingTop24Pixel" size="body-2">${this.workItem.systems.length} systems</sh-text>`}
                        <sh-table id="systems-table">
                            <sh-table-row slot="header">
                                ${this.managing ?
            html `<sh-table-head unsortable columns="1">
                                            <sh-checkbox label="" 
                                                         ?active="${self.selectedSystems.size > 0}"
                                                         ?indeterminate="${self.selectedSystems.size > 0 && self.selectedSystems.size < self.workItem.systems.length}"
                                                         @checked="${self.allSystemSelectionsChanged}"></sh-checkbox>
                                        </sh-table-head>` :
            html ``}
                                <sh-table-head unsortable columns="6">System Name</sh-table-head>
                                <sh-table-head unsortable columns="5">Health System</sh-table-head>
                                <sh-table-head unsortable columns="${self.managing ? '4' : '5'}">Address</sh-table-head>
                                <sh-table-head unsortable columns="3">Type</sh-table-head>
                                <sh-table-head unsortable columns="5">Status</sh-table-head>
                            </sh-table-row>
                            ${this.workItem.systems.map((system) => html `
                            <sh-table-row>
                                ${this.managing ?
            html `<sh-table-cell columns="1">
                                        <sh-checkbox label="" 
                                                     ?active="${self.selectedSystems.has(system)}"
                                                     @checked="${(e) => self.systemSelectionChanged(e, system)}"></sh-checkbox>
                                    </sh-table-cell>` :
            html ``}
                                <sh-table-cell columns="6">
                                    <sh-text>${system.systemName}</sh-text>
                                </sh-table-cell>
                                <sh-table-cell columns="5">
                                    <sh-text>${system.hospitalName}</sh-text>
                                </sh-table-cell>
                                <sh-table-cell columns="${self.managing ? '4' : '5'}">
                                    <sh-text>${system.address}</sh-text>
                                </sh-table-cell>
                                <sh-table-cell columns="3">
                                    <sh-text>${system.isControlSide ? 'Control-side' : 'Patient-side'}</sh-text>
                                </sh-table-cell>
                                <sh-table-cell columns="5">
                                    <cor-system-state state="${system.systemState}"></cor-system-state>
                                </sh-table-cell>
                            </sh-table-row>`)}
                        </sh-table>` :
            html ``}
                </div>
                <div id="CardFooter" slot="footer">
                    <div id="LeftButtons">
                        ${self.managing ? html `
                            <sh-button id="Back" label='Back' color="tertiary"
                                       ?disabled="${self.addingSystems}"
                                       @click=${self.cancelManaging}>
                            </sh-button>
                            <sh-button id="AddSystems" label='Add Systems' color="secondary" size="l"
                                       ?disabled="${self.addingSystems}"
                                       @click=${self.initiateAddingSystems}>
                            </sh-button>` : html ``}
                        ${self.viewing && self.workItem.systems.length == 0 ? html `
                            <sh-button id="DeleteNetwork" label='Delete Network' color="tertiary" size="l"
                                       ?disabled="${self.addingSystems}"
                                       @click=${self.initiateDelete}>
                            </sh-button>` : html ``}
                    </div>
                    <div id="RightButtons">
                        ${self.managing ? html `
                            <sh-button label='Remove Systems' size="l"
                                       ?disabled="${self.selectedSystemsCount === 0 || self.addingSystems}"
                                       @click=${self.initiateRemoveSystems} color="primary">
                            </sh-button>` : html ``}
                        ${self.viewing ? html `
                                <sh-button id="ManageNetwork" label='Manage Network' size="l"
                                    @click=${self.manageNetwork} color="primary">
                                </sh-button>` : html ``}
                    </div>
                </div>

                ${self.deleteItemModalHtml}
                
                <sh-modal label='${self.removeSystemsConfirmationLabel}'
                          sticky
                          modal-height="250px"
                          modal-width="600px"
                          type="alert"
                          ?visible=${self.confirmingRemoveSystems}>
                    <div>
                        <div>
                            <sh-text>Remove systems from:</sh-text>
							<div>
								<sh-text><strong>${this.roboticNetworkName}</strong></sh-text>
							</div>
                        </div>
                        <sh-divider></sh-divider>
                        <div>
                            <sh-text>Systems will be removed:</sh-text>
                        </div>
                        ${this.sortedSelectedSystemNames.map((systemName) => html `
                            <div>
                                <sh-text><strong>${systemName}</strong></sh-text>
                            </div>`)}                      
                    </div>
                    <sh-button slot="footer" color="tertiary" label="Cancel" @click=${self.cancelRemoveSystems}></sh-button>
                    <sh-button slot="footer" color="secondary" label="Remove" @click=${self.doRemoveSystems}></sh-button>
                </sh-modal>
                
                <sh-modal label='Add Systems'
                          sticky
                          modal-height="500x"
                          modal-width="1300px"
                          ?visible=${self.addingSystems}>
                    <div>
                        <sh-tag class="paddingAll" label="${this.selectedSystems.size} selected"></sh-tag>
                        <sh-table id="add-systems-table">
                            <sh-table-row slot="header">
                                <sh-table-head unsortable columns="1">
                                    <sh-checkbox label="" 
                                                 ?active="${self.selectedSystems.size > 0}"
                                                 ?indeterminate="${self.selectedSystems.size > 0 && self.selectedSystems.size < self.availableSystems.length}"
                                                 @checked="${self.addSystemSelectionsChanged}"></sh-checkbox>
                                </sh-table-head>
                                <sh-table-head unsortable columns="6">System Name</sh-table-head>
                                <sh-table-head unsortable columns="5">Health System</sh-table-head>
                                <sh-table-head unsortable columns="4">Address</sh-table-head>
                                <sh-table-head unsortable columns="3">Type</sh-table-head>
                                <sh-table-head unsortable columns="5">Status</sh-table-head>
                            </sh-table-row>
                            ${this.availableSystems.map((system) => html `
                                <sh-table-row>
                                    <sh-table-cell columns="1">
                                        <sh-checkbox label="" 
                                                     ?active="${self.selectedSystems.has(system)}"
                                                     @checked="${(e) => self.systemSelectionChanged(e, system)}"></sh-checkbox>
                                    </sh-table-cell>
                                    <sh-table-cell columns="6">
                                        <sh-text>${system.systemName}</sh-text>
                                    </sh-table-cell>
                                    <sh-table-cell columns="5">
                                        <sh-text>${system.hospitalName}</sh-text>
                                    </sh-table-cell>
                                    <sh-table-cell columns="${self.managing ? '4' : '5'}">
                                        <sh-text>${system.address}</sh-text>
                                    </sh-table-cell>
                                    <sh-table-cell columns="3">
                                        <sh-text>${system.isControlSide ? 'Control-side' : 'Patient-side'}</sh-text>
                                    </sh-table-cell>
                                    <sh-table-cell columns="5">
                                        <cor-system-state state="${system.systemState}"></cor-system-state>
                                    </sh-table-cell>
                                </sh-table-row>`)}
                        </sh-table>
                    </div>
                    <sh-button slot="footer" color="secondary" label="Cancel" @click=${self.cancelAddSystems}></sh-button>
                    <sh-button slot="footer" color="primary" label="Add" @click=${self.doAddSystems}></sh-button>
                </sh-modal>
                
           </sh-card>`;
    }
};
__decorate([
    internalProperty()
], CorPageRoboticNetworksCard.prototype, "availableSystems", void 0);
__decorate([
    internalProperty()
], CorPageRoboticNetworksCard.prototype, "oldRoboticNetworkName", void 0);
__decorate([
    internalProperty()
], CorPageRoboticNetworksCard.prototype, "selectedSystemsCount", null);
__decorate([
    internalProperty()
], CorPageRoboticNetworksCard.prototype, "confirmingRemoveSystems", void 0);
__decorate([
    internalProperty()
], CorPageRoboticNetworksCard.prototype, "addingSystems", void 0);
__decorate([
    internalProperty()
], CorPageRoboticNetworksCard.prototype, "maxSelectedSystems", null);
__decorate([
    property({
        hasChanged: (oldValue, newValue) => {
            return (oldValue === null || oldValue === void 0 ? void 0 : oldValue.size) != (newValue === null || newValue === void 0 ? void 0 : newValue.size);
        }
    })
], CorPageRoboticNetworksCard.prototype, "selectedSystems", void 0);
__decorate([
    internalProperty()
], CorPageRoboticNetworksCard.prototype, "sortedSelectedSystemNames", null);
__decorate([
    internalProperty()
], CorPageRoboticNetworksCard.prototype, "removeSystemsConfirmationLabel", null);
__decorate([
    internalProperty()
], CorPageRoboticNetworksCard.prototype, "roboticNetworkName", null);
CorPageRoboticNetworksCard = CorPageRoboticNetworksCard_1 = __decorate([
    customElement('cor-page-robotic-networks-card')
], CorPageRoboticNetworksCard);
export { CorPageRoboticNetworksCard };
//# sourceMappingURL=CorPageRoboticNetworks.js.map